import React from 'react';

// Components
import { CurrencyCell, NoDataCell, PercentageCell } from '@/views/components/cells';
import { HoldingIcon } from '@/views/components/common';

// Libraries
import classNames from 'classnames';

// Types
import Locale from '@/types/locale';

// Utils
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { formatLocalizedString } from '@/utils/i18n';
import { FormattedMessageV2 } from '@/views/components/i18n';
import { TaxAnalysisOverviewValue } from '@/types/tax-analysis';

type Props = {
  value?: TaxAnalysisOverviewValue;
  className?: string;
  locale?: Locale;
  decimalPlace?: number;
};

const TaxAnalysisOverview: React.FunctionComponent<Props> = ({ value, className, locale, decimalPlace }: Props) => {
  const i18nBasePath = 'Proposal.TaxAnalysis';

  if (!value || !value.taxSettings) {
    return <NoDataCell />;
  }

  const { originalComparisonItem, targetComparisonItem, taxSettings } = value;

  return (
    <div className={classNames('tax-analysis-overview', className)}>
      <div className="overview">
        {originalComparisonItem && targetComparisonItem && (
          <div className="description">
            <FormattedMessageV2 locale={locale} toLocalize={`${i18nBasePath}.Overview.TransitioningFrom`} />
            <strong>
              <HoldingIcon type={originalComparisonItem.type} color={originalComparisonItem.color} />
              {formatLocalizedString(originalComparisonItem.name)}
            </strong>
            <FormattedMessageV2 locale={locale} toLocalize={`${i18nBasePath}.Overview.TransitioningTo`} />
            <strong>
              <HoldingIcon type={targetComparisonItem.type} color={targetComparisonItem.color} />
              {formatLocalizedString(targetComparisonItem.name)}
            </strong>
          </div>
        )}
      </div>

      <div className="summary">
        <FormattedMessageV2 className="title" locale={locale} toLocalize={`${i18nBasePath}.Summary.Title`} />
        <div className="info">
          <div className="row">
            <div className="col" data-testid="st-totalGainLoss">
              <FormattedMessageV2
                className="label"
                locale={locale}
                tagName={ALLOWED_HTML_TAGS.H3}
                toLocalize={`${i18nBasePath}.Summary.ShortTermCapitalGainsRate`}
              />
              <PercentageCell className="value" value={taxSettings?.shortTermCapitalGainsRate} decimalPlace={decimalPlace} />
            </div>
            <div className="col" data-testid="lt-totalGainLoss">
              <FormattedMessageV2
                className="label"
                locale={locale}
                tagName={ALLOWED_HTML_TAGS.H3}
                toLocalize={`${i18nBasePath}.Summary.LongTermCapitalGainsRate`}
              />
              <PercentageCell className="value" value={taxSettings?.longTermCapitalGainsRate} decimalPlace={decimalPlace} />
            </div>
            <div className="col" data-testid="st-gains">
              <FormattedMessageV2 className="label" locale={locale} tagName={ALLOWED_HTML_TAGS.H3} toLocalize={`${i18nBasePath}.Summary.ShortTermYearGain`} />
              <CurrencyCell className="value" locale={locale} decimals={decimalPlace} value={{ value: taxSettings?.shortTermYTDGain }} />
            </div>
            <div className="col" data-testid="st-losses">
              <FormattedMessageV2 className="label" locale={locale} tagName={ALLOWED_HTML_TAGS.H3} toLocalize={`${i18nBasePath}.Summary.ShortTermYearLoss`} />
              <CurrencyCell className="value" locale={locale} decimals={decimalPlace} value={{ value: taxSettings?.shortTermYTDLoss }} />
            </div>
          </div>
          <div className="row">
            <div className="col" data-testid="lt-gains">
              <FormattedMessageV2 className="label" locale={locale} tagName={ALLOWED_HTML_TAGS.H3} toLocalize={`${i18nBasePath}.Summary.LongTermYearGain`} />
              <CurrencyCell className="value" locale={locale} decimals={decimalPlace} value={{ value: taxSettings?.longTermYTDGain }} />
            </div>
            <div className="col" data-testid="lt-losses">
              <FormattedMessageV2 className="label" locale={locale} tagName={ALLOWED_HTML_TAGS.H3} toLocalize={`${i18nBasePath}.Summary.LongTermYearLoss`} />
              <CurrencyCell className="value" locale={locale} decimals={decimalPlace} value={{ value: taxSettings?.longTermYTDLoss }} />
            </div>
            <div className="col" data-testid="st-carryforwardBalance">
              <FormattedMessageV2 className="label" locale={locale} tagName={ALLOWED_HTML_TAGS.H3} toLocalize={`${i18nBasePath}.Summary.ShortTermCarryLoss`} />
              <CurrencyCell className="value" locale={locale} decimals={decimalPlace} value={{ value: taxSettings?.shortTermCarriedForwardLoss }} />
            </div>
            <div className="col" data-testid="lt-carryforwardBalance">
              <FormattedMessageV2 className="label" locale={locale} tagName={ALLOWED_HTML_TAGS.H3} toLocalize={`${i18nBasePath}.Summary.LongTermCarryLoss`} />
              <CurrencyCell className="value" locale={locale} decimals={decimalPlace} value={{ value: taxSettings?.longTermCarriedForwardLoss }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxAnalysisOverview;
